import { logger } from '@/services/logger/logger';
import CallingExtensions, {
  type EndStatus,
  type OnInitialized,
  type OnIncomingCall,
  type ContactIdMatch,
  type CompanyIdMatch,
} from '@hubspot/calling-extensions-sdk';

type EventHandler = (data?: unknown) => void;

export interface HubSpotCallData {
  externalCallId?: string;
  engagementId?: number;
  contactId?: number;
  phoneNumber?: string;
  direction: 'inbound' | 'outbound';
  startTime?: number;
  endTime?: number;
  duration?: number;
  status?: EndStatus;
  recordingUrl?: string;
  notes?: string;
}

export interface HubSpotContact {
  id: number;
  name?: string;
  email?: string;
  phoneNumber?: string;
  properties?: Record<string, any>;
}

class HubSpotService {
  private callingExtensions: CallingExtensions | null = null;
  private isInitialized = false;
  private initPromise: Promise<boolean> | null = null;
  private currentCall: HubSpotCallData | null = null;
  private eventHandlers: Map<string, EventHandler[]> = new Map();

  constructor() {
    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    const options = {
      debugMode: import.meta.env.DEV,
      eventHandlers: {
        onReady: () => {
          this.isInitialized = true;
          this.emit('ready');
        },

        onDialNumber: (event: { phoneNumber: string; ownerId: string }) => {
          this.emit('dialNumber', event);
        },

        onCreateEngagementSucceeded: (event: { engagementId: number }) => {
          logger.info('[HubSpot] Engagement created successfully', { event });
          if (this.currentCall) {
            this.currentCall.engagementId = event.engagementId;
          }
          this.emit('engagementCreated', event);
        },

        onCreateEngagementFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to create engagement', { event });
          this.emit('engagementCreateFailed', event);
        },

        onEngagementCreated: (event: { engagementId: number }) => {
          logger.info('[HubSpot] Engagement created', { event });
          if (this.currentCall) {
            this.currentCall.engagementId = event.engagementId;
          }
          this.emit('engagementCreated', event);
        },

        onUpdateEngagementSucceeded: (event: { engagementId: number }) => {
          logger.info('[HubSpot] Engagement updated successfully', { event });
          this.emit('engagementUpdated', event);
        },

        onUpdateEngagementFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to update engagement', { event });
          this.emit('engagementUpdateFailed', event);
        },

        onVisibilityChanged: (event: {
          isMinimized: boolean;
          isHidden: boolean;
        }) => {
          logger.info('[HubSpot] Visibility changed', { event });
          this.emit('visibilityChanged', event);
        },

        onCallerIdMatchSucceeded: (event: ContactIdMatch | CompanyIdMatch) => {
          logger.info('[HubSpot] Caller ID matched', { event });
          this.emit('callerIdMatched', event);
        },

        onCallerIdMatchFailed: (event: { phoneNumber: string }) => {
          logger.info('[HubSpot] Caller ID match failed', { event });
          this.emit('callerIdMatchFailed', event);
        },

        onInitiateCallIdSucceeded: (event: { callId: string }) => {
          logger.info('[HubSpot] Call ID initiated', { event });
          if (this.currentCall) {
            this.currentCall.externalCallId = event.callId;
          }
          this.emit('callIdInitiated', event);
        },

        onInitiateCallIdFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to initiate call ID', { event });
          this.emit('callIdInitiateFailed', event);
        },

        onNavigateToRecordFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to navigate to record', { event });
          this.emit('navigateToRecordFailed', event);
        },

        onUpdateAssociationsFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to update associations', { event });
          this.emit('updateAssociationsFailed', event);
        },

        onPublishToChannelSucceeded: (event: { channelName: string }) => {
          logger.info('[HubSpot] Published to channel successfully', { event });
          this.emit('publishToChannelSucceeded', event);
        },

        onPublishToChannelFailed: (event: {
          error: string;
          channelName: string;
        }) => {
          logger.error('[HubSpot] Failed to publish to channel', { event });
          this.emit('publishToChannelFailed', event);
        },

        onSetCallState: (event: { callState: string }) => {
          logger.info('[HubSpot] Call state set', { event });
          this.emit('callStateSet', event);
        },

        onSetWidgetUrlFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Failed to set widget URL', { event });
          this.emit('setWidgetUrlFailed', event);
        },

        onEndCall: (event: { callEndStatus: EndStatus }) => {
          logger.info('[HubSpot] Call ended', { event });
          this.emit('endCall', event);
        },

        onFailed: (event: { error: string }) => {
          logger.error('[HubSpot] General failure', { event });
          this.emit('failed', event);
        },

        onFinalizeEngagementSucceeded: (event: { engagementId: number }) => {
          logger.info('[HubSpot] Finalize engagement succeeded', { event });
          this.emit('finalizeEngagementSucceeded', event);
        },

        onFinalizeEngagementFailed: (event: { error: string }) => {
          logger.error('[HubSpot] Finalize engagement failed', { event });
          this.emit('finalizeEngagementFailed', event);
        },
      },
    };

    this.callingExtensions = new CallingExtensions(options);
  }

  initialize(): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve) => {
      if (this.isInitialized) {
        resolve(true);
        return;
      }

      const timeout = setTimeout(() => {
        resolve(false);
        return;
      }, 10000);

      this.on('ready', () => {
        clearTimeout(timeout);
        const initData: OnInitialized = {
          isLoggedIn: true,
          engagementId: undefined,
        };
        this.callingExtensions?.initialized(initData);
        resolve(true);
      });
    });

    return this.initPromise;
  }

  // Event emitter methods
  on<T = unknown>(event: string, handler: (data: T) => void) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)?.push(handler as EventHandler);
  }

  off<T = unknown>(event: string, handler: (data: T) => void) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler as EventHandler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: unknown) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        handler(data);
      }
    }
  }

  // Call management methods (NOT USED FOR NOW)
  async startIncomingCall(
    phoneNumber: string,
    externalCallId: string,
    contactId?: number,
  ): Promise<void> {
    if (!this.callingExtensions) {
      throw new Error('HubSpot not initialized');
    }

    this.currentCall = {
      phoneNumber,
      direction: 'inbound',
      contactId,
      externalCallId,
      startTime: Date.now(),
    };

    try {
      const incomingCallData: OnIncomingCall = {
        externalCallId,
        fromNumber: phoneNumber,
        toNumber: '', // Your app's number
        callStartTime: Date.now(),
        createEngagement: true,
      };

      await this.callingExtensions.incomingCall(incomingCallData);
    } catch (error) {
      logger.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { component: 'hubspot_service', operation: 'incoming_call' },
      });
      throw error;
    }
  }

  async resizeWidget(width: number, height: number): Promise<void> {
    if (!this.callingExtensions) {
      return;
    }

    try {
      await this.callingExtensions.resizeWidget({ width, height });
    } catch (error) {
      logger.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { component: 'hubspot_service', operation: 'resize_widget' },
      });
    }
  }

  getCurrentCall(): HubSpotCallData | null {
    return this.currentCall;
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

export const hubspotService = new HubSpotService();
